import { lazy, useState } from 'react';
import { Box, Text, Group, Badge, Avatar, ScrollArea, useMantineTheme, useComputedColorScheme, Skeleton } from '@mantine/core';
import { IconShoppingCart, IconRefresh } from '@tabler/icons-react';
import { WidgetProps } from '../types';
import { registerWidget } from '../registry';
import { useWidgetData } from '../context/DashboardProvider';
import { FadeIn } from '../../../animations';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// Types
// ============================================================================

interface Order {
  id: string;
  customerName: string;
  customerAvatar?: string;
  amount: number;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  date: string;
  items: number;
}

// ============================================================================
// Mock Data Fetcher
// ============================================================================

async function fetchLatestOrders(): Promise<Order[]> {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 500));

  return [
    { id: '1', customerName: 'John Smith', amount: 250, status: 'pending', date: '2024-01-15', items: 3 },
    { id: '2', customerName: 'Sarah Johnson', amount: 189, status: 'processing', date: '2024-01-15', items: 2 },
    { id: '3', customerName: 'Michael Brown', amount: 520, status: 'shipped', date: '2024-01-14', items: 5 },
    { id: '4', customerName: 'Emily Davis', amount: 99, status: 'delivered', date: '2024-01-14', items: 1 },
    { id: '5', customerName: 'David Wilson', amount: 350, status: 'pending', date: '2024-01-13', items: 4 },
    { id: '6', customerName: 'Lisa Anderson', amount: 175, status: 'processing', date: '2024-01-13', items: 2 },
  ];
}

// ============================================================================
// LatestOrders Widget Component
// ============================================================================

function LatestOrdersWidget({ isCollapsed }: WidgetProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  const { data: orders, isLoading, refetch } = useWidgetData(fetchLatestOrders, {
    refreshInterval: 60000, // Refresh every minute
  });
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  if (isCollapsed) return null;

  const getStatusColor = (status: Order['status']) => {
    const colors: Record<Order['status'], string> = {
      pending: 'yellow',
      processing: 'blue',
      shipped: 'cyan',
      delivered: 'green',
      cancelled: 'red',
    };
    return colors[status];
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (isLoading && !orders) {
    return (
      <Box style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={50} mb="sm" />
        ))}
      </Box>
    );
  }

  return (
    <FadeIn style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <ScrollArea style={{ flex: 1 }}>
        {orders?.map((order, index) => (
          <Box
            key={order.id}
            p="xs"
            mb="xs"
            style={{
              backgroundColor: index % 2 === 0
                ? (colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0])
                : 'transparent',
              borderRadius: theme.radius.sm,
            }}
          >
            <Group justify="space-between" wrap="nowrap">
              <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                <Avatar
                  src={order.customerAvatar}
                  size="sm"
                  radius="xl"
                  color="blue"
                >
                  {order.customerName.charAt(0)}
                </Avatar>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500} lineClamp={1} style={accessibilityStyles}>
                    {order.customerName}
                  </Text>
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    {order.items} {order.items === 1 ? 'item' : 'items'}
                  </Text>
                </Box>
              </Group>
              <Box style={{ textAlign: 'right', flexShrink: 0 }}>
                <Text size="sm" fw={600} style={accessibilityStyles}>
                  {formatCurrency(order.amount)}
                </Text>
                <Badge size="xs" color={getStatusColor(order.status)} variant="light">
                  {order.status}
                </Badge>
              </Box>
            </Group>
          </Box>
        ))}
      </ScrollArea>
    </FadeIn>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'latest-orders',
  name: 'Latest Orders',
  description: 'Recent order activity with customer and status information',
  icon: IconShoppingCart,
  category: 'lists',
  component: lazy(() => Promise.resolve({ default: LatestOrdersWidget })),
  sizes: {
    base: { cols: 12, rows: 5 },
    sm: { cols: 12, rows: 5 },
    md: { cols: 6, rows: 5 },
    lg: { cols: 4, rows: 5 },
    xl: { cols: 4, rows: 5 },
    tv: { cols: 4, rows: 4 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'tall', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 6, rows: 4 },
        md: { cols: 4, rows: 4 },
        lg: { cols: 3, rows: 4 },
        xl: { cols: 3, rows: 4 },
        tv: { cols: 3, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 5 },
        sm: { cols: 12, rows: 5 },
        md: { cols: 6, rows: 5 },
        lg: { cols: 4, rows: 5 },
        xl: { cols: 4, rows: 5 },
        tv: { cols: 4, rows: 5 },
      },
      tall: {
        base: { cols: 12, rows: 7 },
        sm: { cols: 12, rows: 7 },
        md: { cols: 6, rows: 7 },
        lg: { cols: 4, rows: 7 },
        xl: { cols: 4, rows: 7 },
        tv: { cols: 4, rows: 7 },
      },
      wide: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 12, rows: 4 },
        lg: { cols: 8, rows: 4 },
        xl: { cols: 8, rows: 4 },
        tv: { cols: 8, rows: 4 },
      },
    },
  },
  data: {
    sourceType: 'self',
    refreshInterval: 60000,
  },
  hasSettings: true,
});

export default LatestOrdersWidget;
